import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import DebugErrorButton from './DebugErrorButton';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  FileText,
  AlertTriangle,
  Calendar,
  Sparkles,
  Menu,
  X,
  LogOut,
  CreditCard,
  Phone,
  Smartphone,
  Receipt,
  UserCog,
  Search,
  ChevronDown,
  MessageSquare,
  Building2,
  Home,
  Mail,
  Zap,
  Database,
  UserPlus,
  UserCheck,
  Shield,
  ChevronsDownUp,
  ChevronsUpDown,
  Sun,
  Moon,
  FileSignature,
  Calculator,
  Baby,
  IdCard,
  ScanLine,
  CalendarClock,
  FolderOpen,
  Settings2
, Newspaper, MessagesSquare, LayoutList, Store, Landmark } from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, hasModuleAccess, isAdmin, isSuperAdmin, isMaster } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const NAV_SECTIONS_LS_KEY = 'jvservice_nav_sections';
  const defaultExpanded = {
    general: true,
    insurance: true,
    telecom: true,
    luce_gas: true,
    agenti: true,
    amministrazione: true
  };
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = localStorage.getItem(NAV_SECTIONS_LS_KEY);
      if (saved) return { ...defaultExpanded, ...JSON.parse(saved) };
    } catch (e) { /* localStorage non disponibile */ }
    return defaultExpanded;
  });
  // Fatture telefonia scadute e non pagate: contatore rosso su "Scadenze" (e sulla sezione,
  // se chiusa) così non passano inosservate. Si aggiorna al cambio pagina, ogni 5 minuti e
  // subito dopo un pagamento (evento dalla pagina Scadenze).
  const [badges, setBadges] = useState({});

  // Disposizione del menu personalizzata dallo studio (backend: routes/menu_config.py): solo la
  // posizione delle voci; chi vede una pagina resta deciso dal modulo della sua sezione d'origine.
  const [menuOrdine, setMenuOrdine] = useState({});
  const [modificaMenu, setModificaMenu] = useState(false);
  const [bozzaOrdine, setBozzaOrdine] = useState(null);
  const [trascinata, setTrascinata] = useState(null);
  const puoModificareMenu = isAdmin() || isSuperAdmin() || isMaster();
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/menu-config`)
      .then((r) => setMenuOrdine(r.data.ordine || {})).catch(() => {});
  }, []);
  const puoTelefonia = hasModuleAccess('telecom');
  useEffect(() => {
    if (!puoTelefonia) return undefined;
    const carica = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/telecom/scadenze/conteggio`)
      .then((r) => setBadges((b) => ({ ...b, scadenzeTelefonia: r.data.scadute || 0 })))
      .catch(() => {});
    carica();
    const timer = setInterval(carica, 5 * 60 * 1000);
    window.addEventListener('jvservice:scadenze-telefonia', carica);
    return () => { clearInterval(timer); window.removeEventListener('jvservice:scadenze-telefonia', carica); };
  }, [puoTelefonia, location.pathname]);

  // messaggi non letti dei clienti (area clienti)
  const puoGenerale = hasModuleAccess('general');
  useEffect(() => {
    if (!puoGenerale) return undefined;
    const carica = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio/ticket-non-letti`)
      .then((r) => setBadges((b) => ({ ...b, messaggiClienti: r.data.non_letti || 0 })))
      .catch(() => {});
    carica();
    const timer = setInterval(carica, 2 * 60 * 1000);
    window.addEventListener('jvservice:messaggi-clienti', carica);
    return () => { clearInterval(timer); window.removeEventListener('jvservice:messaggi-clienti', carica); };
  }, [puoGenerale, location.pathname]);

  // registrazioni dall'app ("Diventa cliente") da gestire
  useEffect(() => {
    if (!puoGenerale) return undefined;
    const carica = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio/registrazioni/conteggio`)
      .then((r) => setBadges((b) => ({ ...b, registrazioniApp: r.data.in_attesa || 0 })))
      .catch(() => {});
    carica();
    const timer = setInterval(carica, 2 * 60 * 1000);
    window.addEventListener('jvservice:registrazioni-app', carica);
    return () => { clearInterval(timer); window.removeEventListener('jvservice:registrazioni-app', carica); };
  }, [puoGenerale, location.pathname]);

  // annunci dell'app: richieste di punti + annunci da guardare
  useEffect(() => {
    if (!puoGenerale) return undefined;
    const carica = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio/annunci/riepilogo`)
      .then((r) => setBadges((b) => ({ ...b, annunciApp: (r.data.richieste || 0) + (r.data.da_guardare || 0) })))
      .catch(() => {});
    carica();
    const timer = setInterval(carica, 2 * 60 * 1000);
    window.addEventListener('jvservice:annunci-app', carica);
    return () => { clearInterval(timer); window.removeEventListener('jvservice:annunci-app', carica); };
  }, [puoGenerale, location.pathname]);

  const puoUtenze = hasModuleAccess('luce_gas');   // badge scadenze luce e gas
  useEffect(() => {
    if (!puoUtenze) return undefined;
    const carica = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/luce-gas/scadenze/conteggio`)
      .then((r) => setBadges((b) => ({ ...b, scadenzeLuceGas: r.data.in_scadenza || 0 })))
      .catch(() => {});
    carica();
    const timer = setInterval(carica, 5 * 60 * 1000);
    window.addEventListener('jvservice:scadenze-luce-gas', carica);
    return () => { clearInterval(timer); window.removeEventListener('jvservice:scadenze-luce-gas', carica); };
  }, [puoUtenze, location.pathname]);

  // Ricorda apertura/chiusura tendine tra un refresh e l'altro
  useEffect(() => {
    try { localStorage.setItem(NAV_SECTIONS_LS_KEY, JSON.stringify(expandedSections)); } catch (e) { /* noop */ }
  }, [expandedSections]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const navSections = [
    {
      id: 'general',
      title: 'Generale',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      module: 'general',
      items: [
        { path: '/clients', label: 'Anagrafica Clienti', icon: Users },
        { path: '/delega', label: 'Delega', icon: FileText },
        { path: '/scanner-documenti', label: 'Scanner Documenti', icon: ScanLine },
        { path: '/messaggi-clienti', label: 'Messaggi clienti', icon: MessagesSquare, badge: 'messaggiClienti' },
        { path: '/annunci-app', label: 'Annunci app', icon: Store, badge: 'annunciApp' },
        { path: '/registrazioni-app', label: 'Registrazioni app', icon: UserPlus, badge: 'registrazioniApp' },
        { path: '/catalogo-clienti', label: 'Catalogo servizi', icon: LayoutList },
        { path: '/news-clienti', label: 'News area clienti', icon: Newspaper },
        { path: '/nuova-da-bolletta', label: 'Nuova da bolletta', icon: Zap },
      ]
    },
    {
      id: 'insurance',
      title: 'Assicurazioni',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      module: 'insurance',
      items: [
        { path: '/quotes', label: 'Preventivi', icon: FileText },
        { path: '/policies', label: 'Polizze', icon: FileText },
        { path: '/claims', label: 'Sinistri', icon: AlertTriangle },
        { path: '/renewals', label: 'Scadenze', icon: Calendar },
        { path: '/payments', label: 'Pagamenti', icon: CreditCard }
      ]
    },
    {
      id: 'services',
      title: 'Servizi',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      module: 'services',
      items: [
        { path: '/canone-rai', label: 'Canone RAI', icon: FileText },
        { path: '/ospitalita', label: 'Ospitalità', icon: UserCheck, alwaysVisible: true },
        { path: '/mutui', label: 'Mutui/Prestiti', icon: Home },
        { path: '/consulting', label: 'Consulenze', icon: MessageSquare },
        { path: '/dimissioni', label: 'Dimissioni', icon: FileSignature, module: 'patronato' },
        { path: '/permessi-soggiorno', label: 'Permessi di soggiorno', icon: IdCard, module: 'patronato' },
        { path: '/imu', label: 'Calcolo IMU', icon: Calculator, module: 'caf' },
        { path: '/assegno-maternita', label: 'Assegno Maternità', icon: Baby },
        { path: '/servizi/cambio-residenza', label: 'Cambio residenza', icon: Home },
        // pratiche Patronato: nascono qui (permesso "services") e il menu salvato le mette nella sezione Patronato
        { path: '/patronato/test-a2', label: 'Test italiano A2', icon: FileSignature, module: 'patronato' },
        { path: '/patronato/ricongiungimento', label: 'Ricongiungimento familiare', icon: Users, module: 'patronato' }
      ]
    },
    // Patronato e CAF: due sezioni distinte. Partono vuote: le voci ce le sposta lo studio con
    // "Personalizza menu" (ogni voce resta visibile solo a chi ha il permesso della sua sezione d'origine).
    {
      id: 'patronato',
      title: 'Patronato',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      module: 'patronato',
      sempreVisibile: true,
      items: [
        // INPS: una voce sola, dentro tutte le pratiche INPS (NASpI, Assegno Unico, invalidità, bonus…)
        { path: '/patronato/inps', label: 'INPS', icon: Landmark },
      ]
    },
    {
      id: 'caf',
      title: 'CAF',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      module: 'caf',
      sempreVisibile: true,
      items: [
        { path: '/caf/appuntamenti', label: 'Appuntamenti', icon: CalendarClock },
        { path: '/caf/isee', label: 'ISEE', icon: Calculator },
        { path: '/caf/730', label: '730/740 Redditi', icon: Receipt },
      ]
    },
    {
      id: 'utilities',
      title: 'Utenze',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      module: 'utenze',
      items: [
        { path: '/utilities', label: 'Pratiche Utenze', icon: Zap },
        { path: '/sub-agenti', label: 'Sub-Agenti', icon: UserPlus }
      ]
    },
    {
      id: 'luce_gas',
      title: 'LUCE E GAS',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      module: 'luce_gas',
      items: [
        { path: '/luce-gas/fatture', label: 'Fatture estrazione', icon: FileText },
        { path: '/luce-gas/simulazione', label: 'Simulazione offerta', icon: Calculator },
        { path: '/luce-gas/pratiche', label: 'Pratiche', icon: FolderOpen },
        { path: '/luce-gas/scadenze', label: 'Scadenze', icon: CalendarClock, badge: 'scadenzeLuceGas' }
      ]
    },
    {
      id: 'telecom',
      title: 'Internet',
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      module: 'telecom',
      items: [
        { path: '/telecom/contracts', label: 'Contratti', icon: Phone },
        { path: '/telecom/billing', label: 'Fatturazione', icon: Receipt },
        { path: '/telecom/scadenze', label: 'Scadenze', icon: CalendarClock, badge: 'scadenzeTelefonia' }
      ]
    },
    {
      id: 'agenti',
      title: 'Agenti',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      module: null,
      masterOnly: true,
      items: [
        { path: '/agenti', label: 'Elenco Agenti', icon: UserCheck }
      ]
    }
  ];

  // Bottone "Espandi / Comprimi tutto" (desktop + mobile)
  const ALL_SECTION_IDS = [...navSections.map((s) => s.id), 'amministrazione'];

  // ogni voce porta con sé il permesso della sezione d'origine
  const vociPerPercorso = new Map(navSections.flatMap((sez) => sez.items.map((it) => [
    // permesso: quello della voce (module) se c'è, altrimenti quello della sezione d'origine
    it.path, { ...it, _module: it.module ?? sez.module, _masterOnly: !!sez.masterOnly }])));
  const vocePermessa = (it) => (it._masterOnly ? isMaster()
    : (it.alwaysVisible || it._module === null || hasModuleAccess(it._module)));
  const applicaOrdine = (ordine) => {
    const usate = new Set();
    const sezioni = navSections.map((sez) => {
      const items = [];
      ((ordine || {})[sez.id] || []).forEach((p) => {
        const v = vociPerPercorso.get(p);
        if (v && !usate.has(p)) { usate.add(p); items.push(v); }
      });
      return { ...sez, items };
    });
    // voci nuove o non configurate: nella sezione d'origine, in fondo
    navSections.forEach((sez, i) => sez.items.forEach((it) => {
      if (!usate.has(it.path)) { usate.add(it.path); sezioni[i].items.push(vociPerPercorso.get(it.path)); }
    }));
    return sezioni;
  };
  const sezioniMenu = applicaOrdine(modificaMenu ? bozzaOrdine : menuOrdine);

  const iniziaModificaMenu = () => {
    setBozzaOrdine(Object.fromEntries(applicaOrdine(menuOrdine).map((sez) => [sez.id, sez.items.map((it) => it.path)])));
    setModificaMenu(true);
  };
  const spostaVoce = (percorso, sezioneId, primaDi) => {
    setBozzaOrdine((b) => {
      const n = Object.fromEntries(Object.entries(b).map(([k, v]) => [k, v.filter((p) => p !== percorso)]));
      const lista = n[sezioneId] || [];
      const i = primaDi ? lista.indexOf(primaDi) : -1;
      if (i >= 0) lista.splice(i, 0, percorso); else lista.push(percorso);
      n[sezioneId] = lista;
      return n;
    });
  };
  const salvaMenu = async (ordine) => {
    try {
      const r = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/menu-config`, { ordine });
      setMenuOrdine(r.data.ordine || {});
      setModificaMenu(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Salvataggio del menu non riuscito');
    }
  };
  const allExpanded = ALL_SECTION_IDS.every((id) => expandedSections[id]);
  const toggleAllSections = () => {
    const next = {};
    ALL_SECTION_IDS.forEach((id) => { next[id] = !allExpanded; });
    setExpandedSections((prev) => ({ ...prev, ...next }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <img 
              src="/jv-service-logo.png" 
              alt="JV Service" 
              className="h-10 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Passa al tema chiaro' : 'Passa al tema scuro'}
            data-testid="theme-toggle-mobile"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-mobile">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-slate-900 text-white text-sm">{getInitials(user?.full_name)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="flex items-center gap-2">
                <span>{user?.full_name}</span>
                {isAdmin() && <Badge className="bg-amber-100 text-amber-700 text-xs">Admin</Badge>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 flex items-center gap-2">
                <LogOut className="w-4 h-4" /><span>Esci</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        <div className="flex flex-col h-full">
          {/* Logo - Sezione ingrandita */}
          <div className="flex flex-col items-start justify-center gap-2 px-4 py-6 border-b border-slate-100" style={{ minHeight: '140px' }}>
            <img 
              src="/jv-service-logo.png" 
              alt="JV Service" 
              className="w-full h-auto object-contain"
              style={{ maxHeight: '120px', maxWidth: '220px' }}
            />
            {/* Pallino "Ultimi errori API": compare solo quando c'è stato un errore */}
            <DebugErrorButton />
          </div>

          {/* Search - Con margine superiore */}
          <div className="p-4 pt-5 border-b border-slate-100">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 text-sm"
                  data-testid="global-search"
                />
              </div>
            </form>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {/* Dashboard - visibile a tutti */}
              <Link
                to="/dashboard"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  location.pathname === '/dashboard' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="nav-dashboard"
              >
                <LayoutDashboard className="w-5 h-5" />
                <span>Dashboard</span>
              </Link>

            {/* Espandi / Comprimi tutte le tendine */}
            <button
              onClick={toggleAllSections}
              className="flex items-center gap-2 w-full px-4 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-700 transition-colors"
              data-testid="nav-toggle-all"
            >
              {allExpanded
                ? <ChevronsDownUp className="w-3.5 h-3.5" />
                : <ChevronsUpDown className="w-3.5 h-3.5" />}
              <span>{allExpanded ? 'Comprimi tutto' : 'Espandi tutto'}</span>
            </button>

            {/* Module Sections */}
            {modificaMenu && (
              <div className="p-2 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900 space-y-2">
                <p>Trascina le voci su un'altra voce (si mette sopra) o su una sezione (va in fondo).</p>
                <div className="flex flex-wrap gap-1">
                  <button type="button" className="px-2 py-1 rounded bg-slate-900 text-white" onClick={() => salvaMenu(bozzaOrdine)}>Salva</button>
                  <button type="button" className="px-2 py-1 rounded border border-slate-300 bg-white" onClick={() => setModificaMenu(false)}>Annulla</button>
                  <button type="button" className="px-2 py-1 rounded border border-slate-300 bg-white"
                    onClick={() => { if (window.confirm('Ripristinare il menu predefinito per tutti gli utenti?')) salvaMenu({}); }}>Ripristina predefinito</button>
                </div>
              </div>
            )}
            {sezioniMenu.map((section) => {
              // ogni voce si vede col permesso del suo modulo d'origine (anche se spostata)
              const visibleItems = section.items.filter(vocePermessa);
              if (visibleItems.length === 0 && !modificaMenu && !(section.sempreVisibile && puoModificareMenu)) return null;
              const aperta = modificaMenu || expandedSections[section.id];
              const rilascioSezione = (e) => {
                if (!modificaMenu || !trascinata) return;
                e.preventDefault();
                spostaVoce(trascinata, section.id, null);
                setTrascinata(null);
              };
              return (
                <div key={section.id} className="space-y-1"
                  onDragOver={(e) => { if (modificaMenu && trascinata) e.preventDefault(); }}
                  onDrop={rilascioSezione}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`flex items-center justify-between w-full px-4 py-2 text-sm font-semibold ${section.color} rounded-lg ${section.bgColor}`}
                  >
                    <span className="flex items-center gap-2">
                      {section.title}
                      {!expandedSections[section.id] && visibleItems.some((it) => it.badge && badges[it.badge] > 0) && (
                        <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                          {visibleItems.reduce((t, it) => t + (it.badge ? badges[it.badge] || 0 : 0), 0)}
                        </span>
                      )}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections[section.id] ? 'rotate-180' : ''}`} />
                  </button>
                  {aperta && (
                    <div className={`ml-2 space-y-1 ${modificaMenu ? 'min-h-[2rem] rounded-lg border border-dashed border-amber-300 p-1' : ''}`}>
                      {visibleItems.length === 0 && !modificaMenu && (
                        <button type="button" onClick={iniziaModificaMenu}
                          className="w-full text-left px-4 py-2 text-xs text-slate-400 hover:text-slate-700 rounded-lg border border-dashed border-slate-200">
                          Nessuna pagina: clicca qui e trascina dentro le pagine (Personalizza menu)
                        </button>
                      )}
                      {visibleItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            draggable={modificaMenu}
                            onDragStart={(e) => { e.dataTransfer.setData('text/plain', item.path); setTrascinata(item.path); }}
                            onDragEnd={() => setTrascinata(null)}
                            onDragOver={(e) => { if (modificaMenu && trascinata) e.preventDefault(); }}
                            onDrop={(e) => {
                              if (!modificaMenu || !trascinata) return;
                              e.preventDefault();
                              e.stopPropagation();
                              if (trascinata !== item.path) spostaVoce(trascinata, section.id, item.path);
                              setTrascinata(null);
                            }}
                            onClick={(e) => { if (modificaMenu) { e.preventDefault(); return; } setSidebarOpen(false); }}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                              // in "Personalizza menu" la voce della pagina aperta non resta bianco su bianco
                              isActive && !modificaMenu ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                            } ${modificaMenu ? 'cursor-move border border-slate-200 bg-white' : ''} ${trascinata === item.path ? 'opacity-40' : ''}`}
                            data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                          >
                            {modificaMenu && <span className="text-slate-400">⋮⋮</span>}
                            <Icon className="w-4 h-4" />
                            <span>{item.label}</span>
                            {item.badge && badges[item.badge] > 0 && (
                              <span className="ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center" title="Fatture scadute non pagate">
                                {badges[item.badge]}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Amministrazione — sezione a tendina (voci filtrate per ruolo) */}
            {(() => {
              const adminItems = [
                { show: true, path: '/ai-assistant', label: 'AI Assistant', icon: Sparkles },
                { show: isSuperAdmin(), path: '/company-profile', label: 'Profilo Ditta', icon: Building2 },
                { show: isAdmin(), path: '/users', label: 'Gestione Utenti', icon: UserCog },
                { show: isSuperAdmin(), path: '/backup', label: 'Backup & Ripristino', icon: Database },
                { show: isSuperAdmin(), path: '/registro-attivita', label: 'Registro Attività', icon: Shield },
              ].filter((it) => it.show);
              if (adminItems.length === 0) return null;
              const open = expandedSections['amministrazione'];
              return (
                <div className="space-y-1">
                  <button
                    onClick={() => toggleSection('amministrazione')}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm font-semibold text-slate-600 rounded-lg bg-slate-100"
                  >
                    <span>Amministrazione</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="ml-2 space-y-1">
                      {adminItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                              isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                            }`}
                            data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{item.label}</span>
                            {item.badge && badges[item.badge] > 0 && (
                              <span className="ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center" title="Fatture scadute non pagate">
                                {badges[item.badge]}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu-desktop">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-slate-900 text-white">{getInitials(user?.full_name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-slate-900 text-sm truncate">{user?.full_name}</p>
                    <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                      <span className="truncate">{user?.email}</span>
                      {isAdmin() && <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-1">Admin</Badge>}
                    </div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-56">
                {/* tema e personalizzazione del menu: dentro il menu dell'utente (richiesta dello studio) */}
                {puoModificareMenu && !modificaMenu && (
                  <DropdownMenuItem onClick={iniziaModificaMenu} className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4" /><span>Personalizza menu</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={toggleTheme} className="flex items-center gap-2" data-testid="theme-toggle-desktop">
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  <span>{theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 flex items-center gap-2">
                  <LogOut className="w-4 h-4" /><span>Esci</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
